import { useCallback, useEffect, useMemo, useState } from 'react';
import { IId } from 'interfaces/common/common';

import { Select, SubmissionButtons } from '@components/common';

import { useModal } from '@components/common/Modal/utils';

import { TRange } from '@interfaces/common/range';
import { TRequiredSelectOption } from '@interfaces/components/componentTypes';

import { hourlyTimeOptions } from '@constants/timeOptions';

import { isTimeRangeOverlapping } from '../utils';

import styles from './OperationWindowRulesTimeInterval.module.scss';

interface IOperationWindowRulesTimeIntervalProps {
	onSubmit?: (range: TRange<string> & IId) => void;
	existedRanges?: TRange<string>[];
}

const OperationWindowRulesTimeInterval = ({
	onSubmit,
	existedRanges = [],
}: IOperationWindowRulesTimeIntervalProps) => {
	const { onClose } = useModal();

	const [fromTime, setFromTime] = useState<TRequiredSelectOption<string> | null>(null);
	const [toTime, setToTime] = useState<TRequiredSelectOption<string> | null>(null);
	const [timeError, setTimeError] = useState<string>('');

	const isTimeOverlaps = useMemo(() => {
		return fromTime && toTime
			? isTimeRangeOverlapping({ from: fromTime.value, to: toTime.value }, existedRanges)
			: false;
	}, [fromTime, toTime, existedRanges]);

	const fromTimeHandler = useCallback((option: TRequiredSelectOption<string>) => {
		setFromTime(option);
	}, []);

	const toTimeHandler = useCallback((option: TRequiredSelectOption<string>) => {
		setToTime(option);
	}, []);

	useEffect(() => {
		if (!!fromTime && !!toTime) {
			if (fromTime.value >= toTime.value) {
				setTimeError('From time must be less than To time');
			} else if (isTimeOverlaps) {
				setTimeError('Time range overlaps with existing ranges');
			} else {
				setTimeError('');
			}
		} else {
			setTimeError('');
		}
	}, [
		fromTime,
		toTime,
		isTimeOverlaps,
	]);

	const onSubmitHandler = useCallback(() => {
		if (fromTime && toTime && !timeError) {
			const range = {
				from: fromTime.value,
				to: toTime.value,
				id: `${fromTime.label} - ${toTime.label}`,
			};

			onSubmit?.(range);
			onClose();
		}
	}, [fromTime, toTime, timeError, onSubmit, onClose]);

	return (
		<div className={styles.wrapper}>
			<div className={styles.formWrapper}>
				<span>From</span>
				<Select
					handleChange={fromTimeHandler}
					isWithoutUseForms
					name='fromTime'
					options={hourlyTimeOptions}
					placeholder='From'
					value={fromTime}
					width='100px'
				/>
				<span>To</span>
				<Select
					handleChange={toTimeHandler}
					isWithoutUseForms
					name='toTime'
					options={hourlyTimeOptions}
					placeholder='To'
					value={toTime}
					width='100px'
				/>
			</div>
			{timeError && (<div className={styles.errorMessage}>{timeError}</div>)}
			<SubmissionButtons
				disabledPositive={!fromTime || !toTime || !!timeError}
				isWithoutBtnRightPadding
				negativeHandler={onClose}
				positiveHandler={onSubmitHandler}
				positiveLabel='Save'
			/>
		</div>
	);
};

export default OperationWindowRulesTimeInterval;
